import { getPlanPermission } from "@utils/permissions/plan-permissions";
import { AdmPlan, SubjectType } from "@custom-types/base-types";
import { EntityCellParams } from "@components/common/data-grid/adm-data-grid-types";
import { ReactNode, useMemo, useState } from "react";
import {
  DataGridCell,
  DataGridDateCell,
  getEntity,
} from "@components/common/data-grid/adm-data-grid-utils";
import { Box, Grid } from "@mui-wrapper";
import {
  formatCurrentAndMaxValueLimit,
  UNLIMITED,
} from "@utils/limit-data-display";
import { formatSubjectType, getPlanAssigneeName } from "@utils/data-display";
import { DEFAULT_USER_ID, isSceneSeatPlan } from "@utils/plan-utils";
import { formatPlanTableName } from "@utils/create-plan-utils";
import { useAppNavigation } from "@router/use-app-navigation";
import { Chip } from "@flat-ui";
import { DateTimeUtils, UiUtils } from "@stellar/utils";
import {
  isUnlimitedPlanEndDate,
  PLAN_DURATION_LABELS,
} from "@utils/plan-duration";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import { DeactivatePlanDialog } from "@components/plans-table/deactivate-plan-dialog";
import { DeletePlanDialog } from "@components/plans-table/delete-plan-dialog";
import { canDeactivatePlan, canDeletePlan } from "@utils/plan-actions-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import {
  MainPageEvents,
  PlanEvents,
} from "@utils/track-event/track-event-list";
import { TableType } from "@utils/track-event/event-types";
import { DataGridOptionCell } from "@components/common/data-grid/data-grid-option-cell";
import { AddSceneLicenseDialog } from "@components/plans-table/add-scene-license-dialog";
import { UnassignSceneSeatDialog } from "@components/plans-table/unassign-scene-seat-dialog";
import { CopyPlanDialog } from "@components/plans-table/copy-plan-dialog";
import { canCopyPlan } from "@utils/plan-copy-utils";
import { useAppSelector } from "@store/store-helper";
import { appStateSelector } from "@store/app/app-slice";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { DataGridSceneSeatCell } from "@utils/data-grid-utils/DataGrid/data-grid-scene-seat-cell";
import { DataGridTokenCell } from "@utils/data-grid-utils/DataGrid/data-grid-token-cell";

/** Return plan name cell in data-grid */
export function PlanNameCell(params: EntityCellParams<AdmPlan>): ReactNode {
  return <DataGridCell content={formatPlanTableName(getEntity(params))} />;
}

/** Return plan limit cell in data-grid */
export function PlanLimitCell(params: EntityCellParams<AdmPlan>): ReactNode {
  const formattedLimits = getEntity(params).constraints.map(
    formatCurrentAndMaxValueLimit
  );
  const limitsToShow =
    formattedLimits.length > 0 ? formattedLimits : [UNLIMITED];

  return (
    <Grid container sx={{ flexDirection: "column" }}>
      {limitsToShow.map((limit) => (
        <DataGridCell key={limit} content={limit} />
      ))}
      {/* This component is only shown when there are SCENE seats attached to a plan without a
          SCENE_SEAT constraint */}
      <DataGridSceneSeatCell {...params} />
      {/* This component is only shown when there are token plans attached to the plan */}
      <DataGridTokenCell {...params} />
    </Grid>
  );
}

/** Return plan assigned to cell in data-grid */
export function PlanAssignedToCell(
  params: EntityCellParams<AdmPlan>
): ReactNode {
  return (
    <DataGridCell content={formatSubjectType(getEntity(params).subject.type)} />
  );
}

/** Return plan assignee name cell in data-grid */
export function PlanAssigneeNameCell(
  params: EntityCellParams<AdmPlan>
): ReactNode {
  const { trackEvent } = useTrackEvent();
  const plan = getEntity(params);
  const { navigateToProfilePage } = useAppNavigation();

  if (plan.subject.type === "DEVICE") {
    return <DataGridCell content={getPlanAssigneeName(plan)} />;
  }

  /**
   * First assign of scene plan is a special case,
   * because it is assigned to a placeholder user with id DEFAULT_USER_ID.
   * Which navigate to a non-existing user profile page, so we disable the chip in this case.
   */
  const hasAssignedToEmptyUser =
    plan.subject.type === "COMPANY_MEMBER" &&
    plan.subject.id === DEFAULT_USER_ID;

  return (
    <Chip
      disabled={hasAssignedToEmptyUser}
      textTransform="none"
      label={getPlanAssigneeName(plan)}
      onClick={(event) => {
        event.stopPropagation();
        if (plan.subject.type === "COMPANY") {
          trackEvent({
            name: MainPageEvents.openItem,
            props: {
              target: "COMPANY",
              location: "workspace-plans-table",
            },
          });
        } else if (plan.subject.type === "PROJECT") {
          trackEvent({
            name: MainPageEvents.openItem,
            props: {
              target: "PROJECT",
              location: "project-plans-table",
            },
          });
        }
        navigateToProfilePage({
          subjectType: plan.subject.type,
          subjectId: plan.subject.id,
        });
      }}
    />
  );
}

/** Return plan status cell in data-grid */
export function PlanStatusCell(params: EntityCellParams<AdmPlan>): ReactNode {
  const statusIdentifier = getEntity(params).status.identifier;
  const statusChipProps = useMemo(() => {
    switch (statusIdentifier) {
      case "active":
        return {
          label: "Active",
          color: "success" as const,
          tooltip:
            "Current date is within Plan's start and end date, and all limits are met.",
        };

      case "invalid":
        return {
          label: "Invalid",
          color: "info" as const,
          tooltip:
            "Current date is within Plan's start and end date, but some limits are violated.",
        };

      case "inactive":
        return {
          label: "Inactive",
          color: "warning" as const,
          tooltip: "Current date is not within Plan's start and end date.",
        };

      case "deactivated":
        return {
          label: "Deactivated",
          color: "error" as const,
          tooltip:
            "Plan has no practical impact and exists only for documentary purposes.",
        };
    }
  }, [statusIdentifier]);

  // A plan is considered to have a soft limit if it has constraints but all of them are soft (i.e. they have no dates).
  const isSoftLimit =
    getEntity(params).constraints.length > 0 &&
    getEntity(params).constraints.every(
      ({ dates }) => dates.gracePeriod === undefined
    );

  return (
    <>
      <Chip {...statusChipProps} />
      {isSoftLimit && (
        <Box
          component="span"
          title="This is a soft limit, which means that even if the maximum value is reached, the plan will not be deactivated."
          sx={{ ml: "10px" }}
        >
          <InfoIcon
            sx={{ verticalAlign: "text-bottom" }}
            color="info"
            fontSize="small"
          />
        </Box>
      )}
    </>
  );
}

/** Return plan start date cell in data-grid */
export function PlanStartDateCell(
  params: EntityCellParams<AdmPlan>
): ReactNode {
  return <DataGridDateCell date={getEntity(params).startDate} />;
}

/** Return plan expiration date cell in data-grid */
export function PlanExpirationDateCell(
  params: EntityCellParams<AdmPlan>
): ReactNode {
  const endDate = getEntity(params).endDate;

  if (!endDate) {
    return <DataGridCell content={UiUtils.DASH} />;
  }

  if (isUnlimitedPlanEndDate(endDate)) {
    return <DataGridCell content={PLAN_DURATION_LABELS.unlimited.option} />;
  }

  return (
    <DataGridCell
      content={
        <>
          {DateTimeUtils.formatDateTimeInUtc({
            date: endDate,
          })}
          {DateTimeUtils.isInTheLast30Days(endDate) && (
            <Box
              component="span"
              title="Plan expired less than a month ago"
              sx={{ ml: "10px" }}
            >
              <WarningIcon
                sx={{ verticalAlign: "text-bottom" }}
                color="error"
                fontSize="small"
              />
            </Box>
          )}
          {(DateTimeUtils.isInTheNext30Days(endDate) ||
            DateTimeUtils.isToday(endDate)) && (
            <Box
              component="span"
              title="Plan will expire in less than a month"
              sx={{ ml: "10px" }}
            >
              <WarningIcon
                sx={{ verticalAlign: "text-bottom" }}
                color="secondary"
                fontSize="small"
              />
            </Box>
          )}
        </>
      }
    />
  );
}

/** The dialog that is currently open for a plan row, if any */
type OpenPlanDialog =
  | "deactivate-plan"
  | "delete-plan"
  | "add-scene-license"
  | "unassign-scene-seat"
  | "copy-plan";

/** Return plan option cell in data-grid */
export function PlanOptionsCell(params: EntityCellParams<AdmPlan>): ReactNode {
  const { trackEvent } = useTrackEvent();
  const { isDevModeEnabled } = useAppSelector(appStateSelector);
  const [openDialog, setOpenDialog] = useState<OpenPlanDialog | null>(null);
  const entity = getEntity(params);

  return (
    <>
      <DataGridOptionCell
        item={entity}
        menuItems={[
          {
            label: "Add SCENE Seat",
            onSelect: () => {
              trackEvent({
                name: PlanEvents.openAddSceneLicense,
                props: {
                  location: getPlanTableType(entity.subject.type),
                },
              });

              setOpenDialog("add-scene-license");
            },
            shouldShow: (plan) => isDevModeEnabled && canAddSceneLicense(plan),
            permission: getPlanPermission(entity.subject.type, "edit"),
          },
          {
            label: "Unassign Seat",
            onSelect: () => {
              trackEvent({
                name: PlanEvents.openUnassignSceneSeat,
                props: {
                  location: getPlanTableType(entity.subject.type),
                },
              });

              setOpenDialog("unassign-scene-seat");
            },
            shouldShow: (plan) =>
              isDevModeEnabled && canUnassignSceneSeat(plan),
            permission: getPlanPermission(entity.subject.type, "edit"),
          },
          {
            label: "Copy Plan",
            onSelect: () => {
              trackEvent({
                name: PlanEvents.openCopyPlan,
                props: {
                  location: getPlanTableType(entity.subject.type),
                },
              });

              setOpenDialog("copy-plan");
            },
            shouldShow: canCopyPlan,
            permission: getPlanPermission(entity.subject.type, "edit"),
          },
          {
            label: "Deactivate Plan",
            isDangerous: true,
            onSelect: () => {
              trackEvent({
                name: PlanEvents.openDeactivatePlan,
                props: {
                  location: getPlanTableType(entity.subject.type),
                },
              });

              setOpenDialog("deactivate-plan");
            },
            shouldShow: canDeactivatePlan,
            permission: getPlanPermission(entity.subject.type, "edit"),
          },
          {
            label: "Delete Plan",
            isDangerous: true,
            onSelect: () => {
              trackEvent({
                name: PlanEvents.openDeletePlan,
                props: {
                  location: getPlanTableType(entity.subject.type),
                },
              });

              setOpenDialog("delete-plan");
            },
            shouldShow: (plan) => isDevModeEnabled && canDeletePlan(plan),
            permission: getPlanPermission(entity.subject.type, "edit"),
          },
        ]}
      />
      {openDialog === "deactivate-plan" && (
        <DeactivatePlanDialog
          plan={entity}
          subjectType={entity.subject.type}
          onClose={() => setOpenDialog(null)}
        />
      )}
      {openDialog === "delete-plan" && (
        <DeletePlanDialog plan={entity} onClose={() => setOpenDialog(null)} />
      )}
      {openDialog === "add-scene-license" && (
        <AddSceneLicenseDialog
          plan={entity}
          subjectType={entity.subject.type}
          onClose={() => setOpenDialog(null)}
        />
      )}
      {openDialog === "copy-plan" && (
        <CopyPlanDialog plan={entity} onClose={() => setOpenDialog(null)} />
      )}
      {openDialog === "unassign-scene-seat" && (
        <UnassignSceneSeatDialog
          plan={entity}
          onClose={() => setOpenDialog(null)}
        />
      )}
    </>
  );
}

/** Return the plan table type for a given subject type */
export function getPlanTableType(subjectType: SubjectType): TableType {
  switch (subjectType) {
    case "DEVICE":
      return "hardware-licenses-table";
    case "COMPANY":
    case "COMPANY_MEMBER":
      return "workspace-plans-table";
    case "PROJECT":
      return "project-plans-table";

    default:
      throw new Error("Invalid subject type for plans table");
  }
}

/**
 * Whether the SCENE seat this plan stands for can be handed back, which needs the seat to be
 * active and actually held by somebody. An unassigned seat is already free, so there is nothing
 * to take away from anybody.
 */
function canUnassignSceneSeat(plan: AdmPlan): boolean {
  return (
    isSceneSeatPlan(plan) &&
    plan.status.identifier === "active" &&
    plan.subject.id !== DEFAULT_USER_ID
  );
}

/**
 * List of bundle identifiers that are considered can have SCENE seats.
 * If a plan has any of these bundles, it can have SCENE seats added to it.
 */
const SCENE_LICENSE_BUNDLES: FaroCoreApiTypes.BundleIdentifier[] = [
  "sphere-xg-addon-point-cloud",
  "sphere-xg-2026-scan-essential",
  "sphere-xg-2026-scan-professional",
  "sphere-xg-2026-scan-enterprise",
];

/** Whether SCENE seats can be added to the given plan */
function canAddSceneLicense(plan: AdmPlan): boolean {
  return plan.featureBundles.some(({ identifier }) =>
    SCENE_LICENSE_BUNDLES.includes(identifier)
  );
}
